import React, { useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowUpRight,
  Award,
  GraduationCap,
  Microscope,
  Stethoscope,
  CheckCircle2,
  Phone,
  Calendar,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import AppointmentDialog from "@/components/AppointmentDialog";
import { DR_KAMLESH_IMG, DR_DHARMIN_IMG } from "@/lib/assets";
import useSEO from "@/hooks/useSEO";

const SITE = "https://manasneuropsychiatryhospital.in";

const DATA = {
  "dr-kamlesh-shah": {
    name: "Dr. Kamlesh A. Shah",
    title: "Founder · Senior Consultant — Manas Hospital",
    image: DR_KAMLESH_IMG,
    accent: "from-[#F4EFE6] via-[#E2C044]/15 to-[#FDFBF7]",
    qualifications: "MD Neuropsychiatry · MBBS · FIPS · FIAPP",
    speciality: ["Neuropsychiatry", "Deaddiction Specialist", "Sexologist"],
    education: [
      "MBBS — B. J. Medical College, Ahmedabad",
      "MD (Psychiatry) — B. J. Medical College, Ahmedabad",
    ],
    affiliations: [
      "Life Fellow, Indian Psychiatric Society (FIPS)",
      "Fellow, Indian Association of Private Psychiatry (FIAPP)",
    ],
    bio: [
      "Dr. Kamlesh A. Shah is the founding consultant of Manas Neuropsychiatry Hospital — one of Bhavnagar's earliest dedicated psychiatric practices, established in 1989.",
      "With over three decades of clinical experience, Dr. Shah has treated patients across the entire spectrum of psychiatric, neuropsychiatric and sexual-health concerns — from severe mood and psychotic disorders to deaddiction and family-related distress.",
      "His clinical philosophy combines time-tested evidence-based psychiatry with the quieter, harder-to-name skill of being truly present with a patient. He is deeply respected across Saurashtra for the dignity and discretion with which he treats every case.",
    ],
    interests: [
      "Mood &amp; anxiety disorders",
      "Schizophrenia &amp; psychotic illnesses",
      "Adult ADHD &amp; cognitive disorders",
      "Substance use disorders",
      "Sexual health &amp; couple therapy",
      "Family-centred psychiatric care",
    ],
  },
  "dr-dharmin-shah": {
    name: "Dr. Dharmin K. Shah",
    title: "Consultant Interventional Neuropsychiatrist",
    image: DR_DHARMIN_IMG,
    accent: "from-[#F4EFE6] via-[#E07A5F]/15 to-[#FDFBF7]",
    qualifications: "MD Neuropsychiatry · TMS Physician (AIIMS, Delhi) · MBBS · MIPS",
    speciality: [
      "Interventional Neuropsychiatry",
      "Deaddiction Specialist",
      "Sexologist",
    ],
    education: [
      "MBBS — Government Medical College, Bhavnagar",
      "MD (Psychiatry) — SBKS Medical Institute &amp; Research Institute, Vadodara",
      "TMS Physician Training — All India Institute of Medical Sciences (AIIMS), New Delhi",
    ],
    affiliations: [
      "Life Ordinary Member, Indian Psychiatric Society (MIPS)",
      "Founder, Vyasan Mukt Campus — Bhavnagar District",
    ],
    publications: [
      "Annals of Indian Psychiatry",
      "Journal of Psychosexual Health",
    ],
    bio: [
      "Dr. Dharmin K. Shah is a second-generation neuropsychiatrist who has brought interventional neuromodulation — including rTMS, mBPT, tDCS and ketamine IV — to Bhavnagar's first dedicated suite.",
      "Trained as a TMS Physician at AIIMS, New Delhi, Dr. Shah specialises in treatment-resistant depression, OCD, anxiety and addiction-related disorders. His research has been published in <em>Annals of Indian Psychiatry</em> and the <em>Journal of Psychosexual Health</em>.",
      "He is the founder of <strong>Vyasan Mukt Campus</strong> — a sustained, on-the-ground awareness movement that has delivered over 50 awareness campaigns and trained healthcare workers across Bhavnagar district to fight youth addiction.",
      "Dr. Shah is a regular voice in regional and national media — newspapers, magazines and TV channels — on psychiatry, deaddiction and sexology.",
    ],
    interests: [
      "Treatment-resistant depression (rTMS)",
      "Obsessive Compulsive Disorder (OCD)",
      "Deaddiction &amp; relapse prevention",
      "Ketamine IV infusion therapy",
      "Naltrexone implants",
      "Sexology &amp; psychosexual health",
      "Youth addiction prevention",
    ],
  },
};

export default function DoctorDetail() {
  const { slug } = useParams();
  const [bookOpen, setBookOpen] = useState(false);
  const doc = DATA[slug];

  const stripTags = (s) => String(s).replace(/<[^>]+>/g, "").replace(/&amp;/g, "&");

  useSEO({
    title: doc ? `${doc.name} — ${doc.title}` : "Doctor",
    description: doc
      ? `${doc.name} (${doc.qualifications}) at Manas Neuropsychiatry Hospital, Bhavnagar. Speciality: ${doc.speciality.join(", ")}.`
      : "",
    image: doc?.image,
    type: "profile",
    jsonLd: doc
      ? {
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "MedicalWebPage",
              "@id": `${SITE}/doctors/${slug}`,
              url: `${SITE}/doctors/${slug}`,
              name: `${doc.name} — ${doc.title}`,
              description: `${doc.name} (${doc.qualifications}) at Manas Neuropsychiatry Hospital, Bhavnagar.`,
              inLanguage: "en-IN",
              about: { "@id": `${SITE}/doctors/${slug}#physician` },
              mainEntity: { "@id": `${SITE}/doctors/${slug}#physician` },
            },
            {
              "@type": "Physician",
              "@id": `${SITE}/doctors/${slug}#physician`,
              name: doc.name,
              url: `${SITE}/doctors/${slug}`,
              image: doc.image,
              jobTitle: doc.title,
              description: stripTags(doc.bio[0]),
              medicalSpecialty: doc.speciality,
              telephone: "+91-94275-60101",
              email: "manasneuropsychiatryhospital@gmail.com",
              address: {
                "@type": "PostalAddress",
                streetAddress: "2nd floor, Carlton Square, Near Madhavdeep Complex, Kalanala",
                addressLocality: "Bhavnagar",
                postalCode: "364001",
                addressRegion: "Gujarat",
                addressCountry: "IN",
              },
              areaServed: ["Bhavnagar", "Saurashtra", "Gujarat"],
              knowsLanguage: ["Gujarati", "Hindi", "English"],
              hasCredential: doc.qualifications.split(" · ").map((q) => ({
                "@type": "EducationalOccupationalCredential",
                credentialCategory: "degree",
                name: q,
              })),
              alumniOf: doc.education.map((e) => ({
                "@type": "EducationalOrganization",
                name: stripTags(e.split(" — ").slice(-1)[0]),
              })),
              memberOf: doc.affiliations.map((a) => ({
                "@type": "Organization",
                name: stripTags(a),
              })),
              knowsAbout: doc.interests.map(stripTags),
              worksFor: { "@id": `${SITE}/#organization` },
              availableService: doc.speciality.map((s) => ({
                "@type": "MedicalProcedure",
                name: `${s} consultation`,
              })),
            },
            {
              "@type": ["MedicalOrganization", "Hospital"],
              "@id": `${SITE}/#organization`,
              name: "Manas Neuropsychiatry Hospital",
              url: SITE,
              telephone: "+91-94275-60101",
              address: {
                "@type": "PostalAddress",
                streetAddress: "2nd floor, Carlton Square, Near Madhavdeep Complex, Kalanala",
                addressLocality: "Bhavnagar",
                postalCode: "364001",
                addressRegion: "Gujarat",
                addressCountry: "IN",
              },
            },
          ],
        }
      : undefined,
  });

  if (!doc) return <Navigate to="/about" replace />;

  return (
    <div data-testid={`doctor-page-${slug}`}>
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-10 md:pt-14 pb-8">
        <Link
          to="/about"
          data-testid="doctor-back-link"
          className="inline-flex items-center gap-2 text-sm text-[#5C524E] hover:text-[#E07A5F] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back to About
        </Link>
      </section>

      {/* Hero — photo + name */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-16 md:pb-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12 items-center">
          <div className="md:col-span-5">
            <div className={`rounded-[32px] overflow-hidden aspect-[4/5] bg-gradient-to-br ${doc.accent} shadow-[0_30px_80px_rgba(45,38,35,0.18)]`}>
              <img src={doc.image} alt={doc.name} className="w-full h-full object-cover object-top" />
            </div>
          </div>
          <div className="md:col-span-7">
            <p className="eyebrow text-[#A39171] mb-5">{doc.title}</p>
            <h1 className="font-display text-5xl md:text-7xl text-[#2D2623] leading-[0.95] tracking-tight">
              {doc.name}
            </h1>
            <p className="mt-6 text-[#5C524E] text-base md:text-lg">{doc.qualifications}</p>

            <div className="mt-10 flex flex-wrap gap-3">
              {doc.speciality.map((s) => (
                <span
                  key={s}
                  className="px-4 py-2 rounded-full bg-[#F4EFE6] border border-[#A39171]/20 text-sm text-[#2D2623] font-medium"
                >
                  {s}
                </span>
              ))}
            </div>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Button
                data-testid="doctor-book-btn"
                onClick={() => setBookOpen(true)}
                className="rounded-full bg-[#2D2623] hover:bg-[#E07A5F] text-[#FDFBF7] px-8 h-12 font-medium"
              >
                <Calendar className="mr-2 w-4 h-4" /> Book with {doc.name.split(" ")[0]} {doc.name.split(" ")[1]}
              </Button>
              <a
                href="tel:+919427560101"
                data-testid="doctor-call-btn"
                className="rounded-full border border-[#A39171]/60 hover:border-[#2D2623] hover:bg-[#2D2623] hover:text-[#FDFBF7] text-[#2D2623] px-8 h-12 inline-flex items-center justify-center font-medium transition-colors"
              >
                <Phone className="mr-2 w-4 h-4" /> Call clinic
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Bio + Sidebar */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-24 md:pb-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-7">
            <p className="eyebrow text-[#A39171] mb-4">About</p>
            <h2 className="font-display text-3xl md:text-4xl text-[#2D2623] tracking-tight mb-8">
              Three decades of psychiatric practice.
            </h2>
            <div className="space-y-5 text-[#5C524E] text-base md:text-lg leading-relaxed">
              {doc.bio.map((p, i) => (
                <p key={i} dangerouslySetInnerHTML={{ __html: p }} />
              ))}
            </div>

            <div className="mt-12">
              <p className="eyebrow text-[#A39171] mb-5">Clinical interests</p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {doc.interests.map((it) => (
                  <li key={it} className="flex items-start gap-3 text-[#2D2623]">
                    <CheckCircle2 className="w-4 h-4 text-[#E07A5F] mt-1 shrink-0" />
                    <span dangerouslySetInnerHTML={{ __html: it }} />
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <aside className="md:col-span-5 space-y-5">
            <div className="bg-[#F4EFE6] rounded-3xl p-7 border border-[#A39171]/15">
              <div className="flex items-center gap-3 mb-5">
                <span className="w-10 h-10 rounded-2xl bg-[#2D2623] text-[#FDFBF7] flex items-center justify-center">
                  <GraduationCap className="w-4 h-4" />
                </span>
                <p className="eyebrow text-[#A39171]">Education</p>
              </div>
              <ul className="space-y-2.5 text-[#2D2623]">
                {doc.education.map((e) => (
                  <li key={e} className="flex items-start gap-2.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#E07A5F] mt-2 shrink-0" />
                    <span dangerouslySetInnerHTML={{ __html: e }} />
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-[#F4EFE6] rounded-3xl p-7 border border-[#A39171]/15">
              <div className="flex items-center gap-3 mb-5">
                <span className="w-10 h-10 rounded-2xl bg-[#2D2623] text-[#FDFBF7] flex items-center justify-center">
                  <Award className="w-4 h-4" />
                </span>
                <p className="eyebrow text-[#A39171]">Affiliations</p>
              </div>
              <ul className="space-y-2.5 text-[#2D2623]">
                {doc.affiliations.map((a) => (
                  <li key={a} className="flex items-start gap-2.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#E07A5F] mt-2 shrink-0" />
                    <span>{a}</span>
                  </li>
                ))}
              </ul>
            </div>

            {doc.publications && (
              <div className="bg-[#2D2623] text-[#FDFBF7] rounded-3xl p-7 relative overflow-hidden">
                <div className="absolute inset-0 grain opacity-30 pointer-events-none" />
                <div className="flex items-center gap-3 mb-5 relative">
                  <span className="w-10 h-10 rounded-2xl bg-[#E07A5F] text-white flex items-center justify-center">
                    <Microscope className="w-4 h-4" />
                  </span>
                  <p className="eyebrow text-[#E2C044]">Published in</p>
                </div>
                <ul className="space-y-2.5 relative">
                  {doc.publications.map((p) => (
                    <li key={p} className="flex items-start gap-2.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#E07A5F] mt-2 shrink-0" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </aside>
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
          <h2 className="font-display text-3xl md:text-5xl text-[#2D2623] tracking-tight">
            Ready to begin? <span className="italic text-[#E07A5F]">We're here.</span>
          </h2>
          <p className="mt-5 text-[#5C524E] text-lg max-w-2xl mx-auto">
            Book a private consultation — by phone, by walk-in, or online. Care coordinators
            respond within hours.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              data-testid="doctor-cta-book"
              onClick={() => setBookOpen(true)}
              className="rounded-full bg-[#2D2623] hover:bg-[#E07A5F] text-[#FDFBF7] px-8 h-12 font-medium"
            >
              Book Appointment <ArrowUpRight className="ml-1 w-4 h-4" />
            </Button>
            <a
              href="tel:+919427560101"
              className="rounded-full border border-[#2D2623] hover:bg-[#2D2623] hover:text-[#FDFBF7] text-[#2D2623] px-8 h-12 inline-flex items-center justify-center font-medium transition-colors"
            >
              Call +91 94275 60101
            </a>
          </div>
        </div>
      </section>

      <AppointmentDialog open={bookOpen} onOpenChange={setBookOpen} />
    </div>
  );
}
